<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Financial Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Payments</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Payments</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewPayment" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Bulk Payment</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callImportPayments" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Import Bank Statement</span>
                                </a>
                            </li>
                            <li>
                                <a @click="goOnlinePayments" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Online Payments</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="col-12 md:col-12">
                        <div class="card p-fluid">
                            <div class="p-grid">

                                <DataTable
                                    v-model:lazyParams="lazyParams"
                                    :value="payments"
                                    lazy
                                    paginator
                                    size="small"
                                    :ptOptions="{ mergeSections: true, classNameMergeStrategy: 'replace' }"
                                    :pt="{headerCell: { class: 'py-1 px-2' }, bodyCell: { class: 'p-0' }, bodyRow: { class: 'p-0' }}"
                                    :rows="10"
                                    :totalRecords="totalRecords"
                                    :loading="loading"
                                    :rowsPerPageOptions="[10, 20, 50, 100]"
                                    :sortMode="'single'"
                                    :rowHover="true"
                                    :striped-rows="true"
                                    :reorderableColumns="true"
                                    filterDisplay="menu"
                                    responsiveLayout="scroll"
                                    :globalFilterFields="['id', 'uniqueId', 'clientName', 'paid', 'description', 'amount']"
                                    @page="onPage"
                                    @sort="onSort"
                                    @filter="onFilter">

                                    <template #header>
                                        <div class="p-grid">
                                            <div class="grid">
                                                <div class="col-12 lg:col-6 p-2">
                                                    <MultiSelect :modelValue="selectedColumns"
                                                                 :options="columnsFullList"
                                                                 optionLabel="header"
                                                                 @update:modelValue="onToggleColumns"
                                                                 display="chip"
                                                                 placeholder="Select Columns"/>
                                                </div>
                                                <div class="col-12 lg:col-6 p-2">
                                                    <Dropdown v-model="selectedStandardFilter"
                                                              :options="statusTypesLimited"
                                                              optionLabel="name" optionValue="code"
                                                              placeholder="Select a Filter"
                                                              @update:modelValue="onToggleStandardFilters"/>
                                                </div>

                                            </div>

                                            <div class="grid">
                                                <div class="col-12 lg:col-6 p-2">
                                                    <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                            class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                                </div>
                                                <div class="col-12 lg:col-6 p-2">
                                                    <Button type="button" icon="pi pi-replay"
                                                            label="Restore to Original View"
                                                            class="p-button-outlined" @click="restoreToOriginal()"/>
                                                </div>
                                            </div>

                                            <div class="p-col-12 p-md-2">
                                                <span class="p-input-icon-left">
                                                    <i class="pi pi-search"/>
                                                    <InputText v-model="filters['global'].value"
                                                               placeholder="Keyword Search on Payment ID, Invoice ID, Client Name, Client Preferred Name, Payment Date, Description or Amount (Please press enter to search)"
                                                               style="width: 100%"
                                                               @keyup.enter="onSearch"/>

                                                </span>
                                            </div>
                                        </div>
                                    </template>

                                    <template #empty>
                                        No payments found.
                                    </template>

                                    <template #loading>
                                        Loading payments. Please wait.
                                    </template>

                                    <Column
                                        field="selectAnOption"
                                        header=""
                                        :headerStyle="{ width: '2rem' }"
                                        :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                                        <template #body="{ data, index }">
                                            <Button icon="pi pi-ellipsis-v"
                                                    class="p-button-sm p-button-text p-button-rounded m-0"
                                                    @click="$refs['menu' + index].toggle($event)"/>
                                            <Menu
                                                :ref="'menu' + index"
                                                :model="getFilteredItems(data)"
                                                :popup="true"/>
                                        </template>
                                    </Column>

                                    <Column :headerStyle="{ width: '2rem' }"
                                            :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                            field="paid" header="Payment Date" :sortable="true"/>

                                    <template v-for="col in selectedColumns" :key="col.field">

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                                :sortable="col.sortable">
                                            <template #body="{ data }">
                                                <span class="p-column-title">Status</span>
                                                <span v-if="data.status === 'I'"
                                                      :class="'status-badge status-' + data.status">Initialised</span>
                                                <span v-if="data.status === 'P'"
                                                      :class="'status-badge status-' + data.status">Fully Allocated</span>
                                                <span v-if="data.status === 'A'"
                                                      :class="'status-badge status-' + data.status">Partially Allocated</span>
                                            </template>
                                        </Column>

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-else-if="col.field === 'type'" :field="col.field" :header="col.header"
                                                :sortable="col.sortable">
                                            <template #body="{ data }">
                                                <span class="p-column-title">Type</span>
                                                <span v-if="data.type === 'C'"
                                                      :class="'type-badge status-' + data.type">Credit Card</span>
                                                <span v-if="data.type === 'B'"
                                                      :class="'type-badge status-' + data.type">Bank Transfer</span>
                                                <span v-if="data.type === 'H'"
                                                      :class="'type-badge status-' + data.type">Health Insurance</span>
                                                <span v-if="data.type === 'R'"
                                                      :class="'type-badge status-' + data.type">Cryptocurrency</span>
                                                <span v-if="data.type === 'A'"
                                                      :class="'type-badge status-' + data.type">Cash</span>
                                                <span v-if="data.type === 'O'"
                                                      :class="'type-badge status-' + data.type">Other</span>
                                                <span v-if="data.type === 'W'"
                                                      :class="'type-badge status-' + data.type">Write-Off</span>
                                                <span v-if="data.type === 'Z'"
                                                      :class="'type-badge status-' + data.type">Helpmed Hulpfonds</span>
                                            </template>
                                        </Column>

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-else-if="col.field === 'category'" :field="col.field"
                                                :header="col.header"
                                                :sortable="col.sortable">
                                            <template #body="{ data }">
                                                <span class="p-column-title">Category</span>
                                                <span v-if="data.category === 'I'"
                                                      :class="'category-badge status-' + data.category">Individual</span>
                                                <span v-if="data.category === 'B'"
                                                      :class="'category-badge status-' + data.category">Bulk</span>
                                            </template>
                                        </Column>

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-else-if="col.field === 'amount'" :field="col.field"
                                                :header="col.header" :sortable="col.sortable">
                                            <template #body="{ data }">
                                                <span class="p-column-title">Amount</span>
                                                {{ formatAmount(data.amount) }}
                                            </template>
                                        </Column>

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-else :field="col.field" :header="col.header"
                                                :sortable="col.sortable"/>

                                    </template>

                                </DataTable>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Payment {{ this.payment.id }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Payment?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes" optionLabel="name"
                                  optionValue="code" placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" :loading="submitted" class="mr-2 mb-2" @click="deletePayment"></Button>
                    <Button label="Cancel" :loading="submitted" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Import Bank Statement</h3>

                    <div v-if="inputFormat === '014'" class="field">
                        <span>
                           <b>Format of CSV File:</b>
                            <br/>
                            yyyy-MM-dd,amount,description
                        </span>
                    </div>

                    <div class="field">
                        <label for="inputFormat">Input Format</label>
                        <Dropdown v-model="inputFormat" :options="inputFormats" optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="inputPassword">Password (if applicable)</label>
                        <InputText id="inputPassword" v-model.trim="inputPassword" required="true"/>
                    </div>

                    <div class="p-field">
                        <input
                            type="file"
                            ref="fileInput"
                            @change="handleFileUpload"
                            accept=".csv, application/pdf"/>
                    </div>

                    <br/>

                    <div class="p-field">
                        <Button label="Review" :loading="submitted" class="mr-2 mb-2" :disabled="!file"
                                @click="submitFile"></Button>
                    </div>

                    <br/>
                    <div class="p-field">
                        <Button label="Cancel" class="mr-2 mb-2" :loading="submitted"
                                @click="callResetImportPayments"></Button>
                    </div>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Review Imported Transactions from Bank Statement</h3>

                    <DataTable :value="importedPaymentComponents" :paginator="true" class="p-datatable-gridlines"
                               :rows="250"
                               dataKey="id" :rowsPerPageOptions="[250,500,750]" :rowHover="true" :striped-rows="true"
                               :reorderableColumns="true" :loading="loading" responsiveLayout="scroll">

                        <template #empty>
                            No imported payment components found.
                        </template>

                        <template #loading>
                            Loading imported payment components. Please wait.
                        </template>

                        <Column v-if="isBankStatement" field="transactionDate" header="Transaction Date"
                                :sortable="true"/>
                        <Column v-if="!isBankStatement" field="treatmentDate" header="Treatment Date" :sortable="true"/>
                        <Column v-if="!isBankStatement" field="memberName" header="Member Name" :sortable="true"/>
                        <Column v-if="!isBankStatement" field="patientName" header="Patient Name" :sortable="true"/>
                        <Column field="description" header="Description" :sortable="true"/>

                        <Column v-if="isBankStatement" field="amount" header="Amount" :sortable="true">
                            <template #body="{ data }">
                                <span class="p-column-title">Amount</span>
                                {{ formatAmount(data.amount) }}
                            </template>
                        </Column>

                        <Column v-if="!isBankStatement" field="amountClaimed" header="Amount Claimed" :sortable="true">
                            <template #body="{ data }">
                                <span class="p-column-title">Amount Claimed</span>
                                {{ formatAmount(data.amountClaimed) }}
                            </template>
                        </Column>

                        <Column field="status" header="Status" :sortable="true">
                            <template #body="{ data }">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'M'"
                                      :class="'status-badge status-' + data.status">Matched</span>
                                <span v-if="data.status === 'N'" :class="'status-badge status-' + data.status">Not
                                    Matched</span>
                            </template>
                        </Column>

                        <Column v-if="!isBankStatement" field="invoiceComponentId" header="Invoice Component #"
                                :sortable="true"/>
                        <Column v-if="isBankStatement" field="invoiceId" header="Invoice #" :sortable="true"/>
                        <Column v-if="isBankStatement" field="clientName" header="Client Name" :sortable="true"/>


                    </DataTable>

                    <Button label="Import Transactions" class="mr-2 mb-2" :loading="submitted"
                            @click="importFinancialTransactions"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted"
                            @click="callResetImportPayments"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Bulk Payment</h3>

                    <div class="field">
                        <label for="client">Client</label>
                        <AutoComplete placeholder="Search" :dropdown="false" :multiple="false"
                                      v-model="newPayment.client" :suggestions="filteredClients"
                                      @complete="searchClients($event)"
                                      field="name"/>
                    </div>

                    <div class="field">
                        <label for="paid">Paid Date</label>
                        <Calendar id="paid" v-model="parsedPaidForNewPayment" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newPayment.type" :options="paymentTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="newPayment.description" required="true"/>
                    </div>

                    <div class="field">
                        <label for="amount">Amount</label>
                        <InputText id="amount" v-model.trim="newPayment.amount" required="true"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :disabled="!canCreateBulkPayment" :loading="submitted"
                            @click="createPayment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>


                </div>
            </div>
        </div>

        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Allocate Bulk Payment</h3>

                    <div style="height: 15px;"></div>

                    <div class="field">
                        <span>
                           <b>Note : The full amount for each invoice needs to be used, and not partial amounts.</b>
                        </span>
                    </div>

                    <div style="height: 15px;"></div>

                    <div class="field">
                        <label for="clientName">Client Name</label>
                        <InputText id="clientName" v-model.trim="payment.clientName" :disabled="true" required="true"/>
                    </div>

                    <div class="field">
                        <label>Total Amount</label>
                        <InputText :modelValue="formatAmount(payment.amount)" disabled/>
                    </div>

                    <div class="field">
                        <label>Allocated Amount</label>
                        <InputText :modelValue="formatAmount(getTotalAllocated())" disabled/>
                    </div>

                    <div class="field">
                        <label>Unallocated Amount</label>
                        <InputText :modelValue="formatAmount(getUnallocatedAmount())" disabled/>
                    </div>

                    <div style="height: 30px;"></div>

                    <div class="card">
                        <DataTable :value="unpaidInvoices" scrollable scrollHeight="400px"
                                   editMode="cell"
                                   @cell-edit-complete="onCellEditComplete" class="mt-2">

                            <Column :headerStyle="{ width: '2rem' }"
                                    :bodyStyle="{ width: '2rem', padding: '0.25rem' }"
                                    field="selectAnOption" style="width: 5%">
                                <template #body="{ data }">
                                    <Button v-tooltip="'Select Full Amount'" icon="pi pi-plus"
                                            class="p-button-rounded p-button-success m-1"
                                            @click="callSelectFullAmount(data)"/>
                                </template>
                            </Column>

                            <Column field="uniqueId" header="Invoice #" style="min-width: 50px" class="font-bold"/>
                            <Column field="clientName" header="Client Name" style="min-width: 100px"/>

                            <Column field="amount" header="Invoice Amount">
                                <template #body="{ data }">
                                    <span class="p-column-title">Invoice Amount</span>
                                    {{ formatAmount(data.amount) }}
                                </template>
                            </Column>

                            <Column field="amountAllocated" header="Amount to Allocate" style="min-width: 120px">
                                <template #editor="{ data, field }">
                                    <InputNumber
                                        v-model="data[field]"
                                        mode="decimal"
                                        :min="0"
                                        :max="getMaxAllocation(data)"
                                        :minFractionDigits="2"
                                        :maxFractionDigits="2"
                                        :useGrouping="true"
                                    />
                                </template>

                                <template #body="{ data }">
                                    {{ formatAmount(data.amountAllocated) }}
                                </template>
                            </Column>

                            <Column header="Amount Remaining">
                                <template #body="{ data }">
                                    <span class="p-column-title">Amount Remaining</span>
                                    {{ formatAmount(data.amount - (data.amountAllocated || 0)) }}
                                </template>
                            </Column>

                        </DataTable>
                    </div>

                    <div style="height: 30px;"></div>

                    <Button label="Allocate Payments" :loading="submitted" class="mr-2 mb-2"
                            @click="allocatePayment"></Button>
                    <Button label="Cancel" :loading="submitted" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Bulk Payment</h3>

                    <div class="field">
                        <label for="clientName">Client Name</label>
                        <InputText id="clientName" v-model.trim="payment.clientName" :disabled="true" required="true"/>
                    </div>

                    <div class="field">
                        <label for="paid">Paid Date</label>
                        <Calendar id="paid" v-model="parsedPaidForExistingPayment" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="payment.type" :options="paymentTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="payment.description" required="true"/>
                    </div>

                    <div class="field">
                        <label for="amount">Amount</label>
                        <InputText id="amount" v-model.trim="payment.amount" required="true"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" @click="updatePayment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>


                </div>
            </div>
        </div>


        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Details for Payment {{ this.paymentDetail.paymentId }}</h3>

                    <div class="field">
                        <label>Total Amount</label>
                        <InputText :modelValue="formatAmount(paymentDetail.totalAmount)" disabled/>
                    </div>

                    <div class="field">
                        <label>Allocated Amount</label>
                        <InputText :modelValue="formatAmount(paymentDetail.totalAmountAllocated)" disabled/>
                    </div>

                    <div class="field">
                        <label>Unallocated Amount</label>
                        <InputText
                            :modelValue="formatAmount(paymentDetail.totalAmount - paymentDetail.totalAmountAllocated)"
                            disabled/>
                    </div>

                    <div style="height: 30px;"></div>

                    <div class="card">
                        <DataTable :value="paymentDetail.allocations" scrollable scrollHeight="400px"
                                   class="mt-2">

                            <Column field="uniqueId" header="Invoice #" style="min-width: 50px" class="font-bold"/>

                            <Column field="amount" header="Invoice Amount">
                                <template #body="{ data }">
                                    <span class="p-column-title">Invoice Amount</span>
                                    {{ formatAmount(data.amount) }}
                                </template>
                            </Column>

                            <Column field="amountAllocated" header="Amount Allocated">
                                <template #body="{ data }">
                                    <span class="p-column-title">Amount Allocated</span>
                                    {{ formatAmount(data.amountAllocated) }}
                                </template>
                            </Column>

                        </DataTable>
                    </div>

                    <div style="height: 30px;"></div>

                    <Button label="Cancel" :loading="submitted" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import PaymentService from "@/service/PaymentService";
import numeral from "numeral";
import axios from "axios";
import PaymentImportService from "../../service/PaymentImportService";
import InvoiceService from "@/service/InvoiceService";
import moment from "moment/moment";
import ClientService from "@/service/ClientService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'id', header: 'Payment #', sortable: true},
            {field: 'uniqueId', header: 'Invoice #', sortable: false},
            {field: 'amount', header: 'Amount', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'category', header: 'Category', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'clientName', header: 'Client Name', sortable: true},
        ];

        this.columnsFullList = [
            {field: 'id', header: 'Payment #', sortable: true},
            {field: 'uniqueId', header: 'Invoice #', sortable: false},
            {field: 'amount', header: 'Amount', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'category', header: 'Category', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'clientName', header: 'Client Name', sortable: true},
            {field: 'description', header: 'Description', sortable: true},
            {field: 'appointmentDate', header: 'Appointment Date', sortable: true},
            {field: 'practitionerName', header: 'Practitioner', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'unallocated', header: 'Unallocated'},
            {field: 'fullyAllocated', header: 'Fully Allocated'},
            {field: 'partiallyAllocated', header: 'Partially Allocated'}
        ];

    },
    async mounted() {
        try {
            await UserService.getUserPing();
        } catch (e) {
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        await this.loadCompanyContext();
    },
    data() {

        return {
            totalRecords: 0,
            loading: false,
            lazyParams: {
                first: 0,
                rows: 10,
                sortField: null,
                sortOrder: null,
                filters: {}
            },
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            submitted: false,
            filteredClients: [],
            newPayment: {},
            payment: {},
            payments: null,
            unpaidInvoices: [],
            paymentDetail: [],
            originalPayments: null,
            importedPayment: {},
            importedPaymentComponents: [],
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            inputPassword: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            categoryTypes: [
                {name: 'Individual', code: 'I'},
                {name: 'Bulk', code: 'B'}
            ],
            statusTypesLimited: [
                {name: 'All', code: 'C'},
                {name: 'Fully Paid', code: 'P'},
                {name: 'Partially Paid', code: 'A'},
                {name: 'Unpaid', code: 'I'}
            ],
            paymentTypes: [
                {name: 'Credit Card', code: 'C'},
                {name: 'Bank Transfer', code: 'B'},
                {name: 'Health Insurance', code: 'H'},
                {name: 'Cryptocurrency', code: 'R'},
                {name: 'Cash', code: 'A'},
                {name: 'Other', code: 'O'},
                {name: 'Write-Off', code: 'W'},
                {name: 'Credit', code: 'E'},
                {name: 'Helpmed Hulpfonds', code: 'Z'}
            ],
            inputFormat: null,
            file: null,
            url: "/payment1/import/healthinsurance/discovery/1",
            fileAccept: '.csv, application/pdf',
            inputFormats: [
                {name: 'Standard Bank Statement (CSV)', code: '010'},
                {name: 'Nedbank Bank Statement (CSV)', code: '011'},
                {name: 'FNB Bank Statement (CSV)', code: '012'},
                // {name: 'ABSA Bank Statement (PDF)', code: '003'},
                // {name: 'FNB Bank Statement (PDF)', code: '004'},
                {name: 'ABSA Bank Statement (CSV)', code: '015'},
                {name: 'Generic Bank Statement (CSV)', code: '014'}
            ],
        }
    },
    watch: {
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                await this.loadCompanyContext();
            }
        }
    },
    computed: {
        isBankStatement() {
            return this.importedPaymentComponents.some(payment => payment.type === "B");
        },
        parsedPaidForNewPayment: {
            get() {
                return new Date(this.newPayment.paid);
            },
            set(value) {
                this.newPayment.paid = value instanceof Date ? value.toISOString() : '';
            }
        },
        parsedPaidForExistingPayment: {
            get() {
                return new Date(this.payment.paid);
            },
            set(value) {
                this.payment.paid = value instanceof Date ? value.toISOString() : '';
            }
        },
        canCreateBulkPayment() {
            if ((this.newPayment.client) && (this.newPayment.type) && (this.newPayment.paid) &&
                (this.newPayment.description) && (this.newPayment.amount)) {
                return true;
            } else {
                return false;
            }
        },
    },
    methods: {
        async loadCompanyContext() {
            try {
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            if (!this.userStore.userConfiguration) {
                const userConfigurationData = await UserService.getUserConfig(this.userStore.user.id);
                this.userStore.setUserConfiguration(userConfigurationData.data);
            }

            this.userConfiguration = this.userStore.userConfiguration;


            if (!this.userStore.companyConfiguration) {
                const companyConfigurationData = await CompanyService.getCompanyConfiguration(this.profile.companyId);
                this.userStore.setCompanyConfiguration(companyConfigurationData.data);
            }

            this.companyConfiguration = this.userStore.companyConfiguration;

            await this.loadData();

            this.loading = false;
        },
        async loadData() {
            this.loading = true;
            try {
                if (this.userStore.user.rights === 'M') {
                    const response = await PaymentService.getPractitionerPaymentsLazyLoading(
                        this.profile.companyId,
                        this.userStore.user.practitionerId,
                        this.lazyParams,
                        this.filters['global']?.value || '',
                        this.selectedStandardFilter, null, null);

                    this.payments = Array.isArray(response.data.content) ? response.data.content : [];
                    this.originalPayments = this.payments;

                    this.totalRecords = response.data?.totalElements || 0;

                } else {
                    const response = await PaymentService.getCompanyPaymentsLazyLoading(
                        this.profile.companyId,
                        this.lazyParams,
                        this.filters['global']?.value || '',
                        this.selectedStandardFilter, null, null);

                    this.payments = Array.isArray(response.data.content) ? response.data.content : [];
                    this.originalPayments = this.payments;

                    this.totalRecords = response.data?.totalElements || 0;
                }

            } catch (error) {
                this.payments = [];
                this.originalPayments = [];
                this.totalRecords = 0;
            }
            this.loading = false;
        },
        onPage(event) {
            this.lazyParams.first = event.first;
            this.lazyParams.rows = event.rows;
            this.loadData();
        },
        onSort(event) {
            this.lazyParams.sortField = event.sortField;
            this.lazyParams.sortOrder = event.sortOrder;
            this.loadData();
        },
        onFilter(event) {
            this.lazyParams.filters = event.filters;
            this.loadData();
        },
        onSearch() {
            this.lazyParams.first = 0;
            this.loadData();
        },
        onToggleStandardFilters() {
            this.lazyParams.first = 0;
            this.loadData();
        },
        clearFilter() {
            this.initFilters();
            this.lazyParams.filters = {};
            this.lazyParams.first = 0;
            this.loadData();
        },
        restoreToOriginal() {
            this.initFilters();
            this.lazyParams.filters = {};
            this.lazyParams.first = 0;
            this.loadData();
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'uniqueId': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'clientName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'paid': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'amount': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        callReset() {
            this.activeTab = 1;
        },
        goOnlinePayments() {
            this.$router.push("commonOnlinePayments");
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        getFilteredItems(data) {
            const items = [];

            if ((data.category === 'B') && (data.status === 'I')) {
                items.push({
                    label: 'Update Payment',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callUpdatePayment(data);
                    }
                });
            }

            if ((data.category === 'B') && (data.status !== 'I')) {
                items.push({
                    label: 'View Details',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callViewPayment(data);
                    }
                });
            }

            if ((data.category === 'B') && ( (data.status !== 'I') | (data.status !== 'P'))) {
                items.push({
                    label: 'Allocate Payment',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callAllocatePayment(data);
                    }
                });
            }

            if ( ((data.category === 'I')) || ((data.category === 'B') && (data.status === 'I'))) {
                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeletePayment(data);
                    }
                });
            }

            return items;
        },
        chooseBlankSelection() {
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            } else {
                return numeral(0.00).format('0,0.00');
            }
        },
        searchClients(event) {
            ClientService.searchCompanyClientsActive(this.profile.companyId, event.query.toLowerCase())
                .then(data => {
                    this.filteredClients = data.data;
                })
        },
        callNewPayment() {
            this.newPayment = {
                category: 'B',
                type: 'B',
                paid: moment().startOf('hour').toDate()
            };

            this.activeTab = 5;
        },
        callImportPayments() {
            this.activeTab = 3;
            this.inputFormat = '001';
        },
        callResetImportPayments() {
            this.activeTab = 1;
            this.resetFileInput()
            this.inputFormat = '001';
        },
        onCellEditComplete(event) {
            const {data, newValue, field} = event;

            data[field] = newValue;
        },
        getTotalAllocated() {
            return this.unpaidInvoices.reduce((sum, invoice) => {
                return sum + (invoice.amountAllocated || 0);
            }, 0);
        },
        getUnallocatedAmount() {
            return this.payment.amount - this.getTotalAllocated();
        },
        callSelectFullAmount(invoice) {

            const unallocated = this.getUnallocatedAmount();

            const invoiceRemaining =
                invoice.amount - (invoice.amountAllocated || 0);

            const allocation = Math.min(unallocated, invoiceRemaining);

            if (allocation <= 0) {
                return;
            }

            invoice.amountAllocated =
                (invoice.amountAllocated || 0) + allocation;
        },
        getMaxAllocation(invoice) {

            const totalAllocated = this.getTotalAllocated();

            const paymentRemaining =
                this.payment.amount - totalAllocated + (invoice.amountAllocated || 0);

            const invoiceRemaining =
                invoice.amount;

            return Math.min(paymentRemaining, invoiceRemaining);
        },
        async allocatePayment() {
            try {
                this.submitted = true;

                const allocations = this.unpaidInvoices
                    .filter(i => (i.amountAllocated || 0) > 0)
                    .map(i => ({
                        invoiceId: i.id,
                        amountAllocated: i.amountAllocated
                    }));

                const payload = {
                    paymentId: this.payment.id,
                    allocations: allocations
                };

                await PaymentService.allocatePayment(this.profile.companyId, payload);

                await this.loadData();

                this.$toast.add({
                    severity: 'success',
                    summary: 'Successful',
                    detail: 'Allocated payment',
                    life: 3000
                });
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to allocate payment. Please try again.',
                    life: 3000
                });
            }

            this.submitted = false;
            this.activeTab = 1;
        },
        handleFileUpload(event) {
            // this.file = event.target.files[0];
            const file = event.target.files[0];

            if (!file) {
                this.file = null;
                return;
            }

            const allowedTypes = [
                'application/pdf',
                'text/csv',
                'application/vnd.ms-excel'
            ];

            const allowedExtensions = ['pdf', 'csv'];
            const extension = file.name.split('.').pop().toLowerCase();

            if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Invalid file type',
                    detail: 'Only PDF and CSV files are allowed.',
                    life: 3000
                });

                this.resetFileInput();
                return;
            }

            this.file = file;
        },
        resetFileInput() {
            this.file = null;
            this.$refs.fileInput.value = ''; // clears input element
        },
        async submitFile() {
            this.submitted = true;

            if (!this.file) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No file selected.',
                    life: 3000
                });
            }

            const formData = new FormData();
            formData.append('file', this.file);

            var url = "/api/paymentimport/import/generic/" + this.profile.companyId;

            if (this.inputFormat === "010") {
                url = "/api/paymentimport/import/bankstatement/za/standardbank/csv/" + this.profile.companyId;
            } else if (this.inputFormat === "011") {
                url = "/api/paymentimport/import/bankstatement/za/nedbank/csv/" + this.profile.companyId;
            } else if (this.inputFormat === "012") {
                url = "/api/paymentimport/import/bankstatement/za/fnb/csv/" + this.profile.companyId;
            } else if (this.inputFormat === "014") {
                url = "/api/paymentimport/import/bankstatement/generic/csv/" + this.profile.companyId;
            } else if (this.inputFormat === "015") {
                url = "/api/paymentimport/import/bankstatement/za/absa/csv/" + this.profile.companyId;
            } else if (this.inputFormat === "050") {
                url = "/api/paymentimport/import/healthinsurance/discovery/pdf/" + this.profile.companyId;
            } else if (this.inputFormat === "001") {
                url = "/api/paymentimport/import/healthinsurance/discovery/pdf/" + this.profile.companyId;
            } else if (this.inputFormat === "002") {
                url = "/api/paymentimport/import/bankstatement/generic/csv/" + this.profile.companyId;
            } else if (this.inputFormat === "003") {
                url = "/api/paymentimport/import/bankstatement/absa/pdf/" + this.profile.companyId + "/" + this.inputPassword;
            } else if (this.inputFormat === "004") {
                url = "/api/paymentimport/import/bankstatement/fnb/pdf/" + this.profile.companyId + "/" + this.inputPassword;
            } else if (this.inputFormat === "005") {
                url = "/api/paymentimport/import/creditcardstatement/absa/pdf/" + this.profile.companyId + "/" + this.inputPassword;
            }

            try {
                const response = await axios.post(url, formData, {
                    headers: {
                        'Authorization': `Bearer ${this.userStore.accessToken}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });

                this.$toast.add({
                    severity: 'success',
                    summary: 'Successful',
                    detail: 'Loaded payments',
                    life: 3000
                });

                this.importedPayment = response.data;
                this.importedPaymentComponents = response.data.components;
                this.activeTab = 4;

            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'File upload NOT successful',
                    life: 3000
                });
            }

            this.resetFileInput();
            this.submitted = false;
        },
        async callAllocatePayment(payment) {
            this.activeTab = 6;
            this.payment = {...payment};

            const [invoiceData] = await Promise.all([
                InvoiceService.getCompanyUnpaidInvoicesForClient(this.profile.companyId, this.payment.clientId)
            ]);

            this.unpaidInvoices = invoiceData.data;

            this.unpaidInvoices = invoiceData.data.map(invoice => ({
                ...invoice,
                amountAllocated: 0
            }));
        },
        callUpdatePayment(payment) {
            this.payment = {...payment};
            this.activeTab = 7;
        },
        async callViewPayment(payment) {
            this.payment = {...payment};
            this.activeTab = 8;

            const [paymentDetailData] = await Promise.all([
                PaymentService.getPaymentDetail(this.payment.id)
            ]);

            this.paymentDetail = paymentDetailData.data;
        },
        callDeletePayment(payment) {
            this.payment = {...payment};
            this.confirmationResponse = 'N';
            this.activeTab = 2;
        },
        async createPayment() {
            this.submitted = true;

            if (this.newPayment.description.trim()) {

                try {

                    var tempClient = "";

                    try {
                        tempClient = this.newPayment.client.id;
                    } catch (error) {
                        tempClient = "";
                    }

                    var paymentDto = {
                        clientId: tempClient,
                        description: this.newPayment.description,
                        amount: this.newPayment.amount,
                        category: this.newPayment.category,
                        type: this.newPayment.type,
                        paid: moment(this.newPayment.paid).format('YYYY-MM-DD HH:mm'),
                        status: 'I'
                    };

                    await PaymentService.createPayment(this.profile.companyId, paymentDto);

                    await this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created payment',
                        life: 3000
                    });
                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create payment. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.product = {};
            this.submitted = false;

        },
        async updatePayment() {

            this.submitted = true;

            if (this.payment.id) {

                try {

                    var paymentDto = {
                        description: this.payment.description,
                        amount: this.payment.amount,
                        category: this.payment.category,
                        type: this.payment.type,
                        paid: moment(this.payment.paid).format('YYYY-MM-DD HH:mm'),
                        status: this.payment.status
                    };

                    await PaymentService.updatePayment(this.payment.id, paymentDto);

                    await this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated payment',
                        life: 3000
                    });
                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update payment. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.product = {};
            this.submitted = false;

        },
        async importFinancialTransactions() {
            this.submitted = true;

            try {
                if ((this.inputFormat === "010") ||
                    (this.inputFormat === "011") ||
                    (this.inputFormat === "012") ||
                    (this.inputFormat === "014") ||
                    (this.inputFormat === "003") ||
                    (this.inputFormat === "015")) {

                    await PaymentImportService.importFinancialTransactions(this.profile.companyId, 'B',
                        this.importedPayment);

                } else {
                    await PaymentImportService.importFinancialTransactions(this.profile.companyId, 'H', this.importedPayment);
                }

                const [paymentData] = await Promise.all([
                    PaymentService.getCompanyPayments(this.profile.companyId)
                ]);

                this.payments = paymentData.data;
                this.originalPayments = paymentData.data;
                this.userStore.setPayments(paymentData.data);

                this.$toast.add({
                    severity: 'success',
                    summary: 'Successful',
                    detail: 'Imported financial transactions',
                    life: 3000
                });

            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to import financial transactions. Please try again.',
                    life: 3000
                });
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deletePayment() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.payment.id) {

                    try {
                        await PaymentService.deletePayment(this.payment.id);

                        const [paymentData] = await Promise.all([
                            PaymentService.getCompanyPayments(this.profile.companyId)
                        ]);

                        this.payments = paymentData.data;
                        this.originalPayments = paymentData.data;
                        this.userStore.setPayments(paymentData.data);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted payment',
                            life: 3000
                        });

                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete payment. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .type-badge, .category-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-P {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-A {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-S {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-F {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-M {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-H {
        background: #FFD8B2;
        color: #805B36;
    }

    &.status-W {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-I {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-A {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-R {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-A {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-M {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-N {
        background: #FFCDD2;
        color: #C63737;
    }

}

.type-badge {
    &.status-C {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-B {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-H {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-R {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-A {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-O {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-W {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-Z {
        background: #C8E6C9;
        color: #256029;
    }

}

.category-badge {
    &.status-I {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-B {
        background: #B3E5FC;
        color: #23547B;
    }
}

.tabview-custom {

    i,
    span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {

    i,
    span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}
</style>
